/**
 * Key_Store — platform-aware persistent store for Identity_Key private keys.
 *
 * The Web_Client touches private key material only through this abstraction
 * (see Requirement 4 in the link-workspace spec). Each adapter (web,
 * desktop, mobile) implements this interface against its native secure
 * storage backend.
 *
 * Key invariants:
 * - Operations are keyed by the external Account ID (the base32 form of the
 *   first 16 bytes of `SHA-256(public_key)`).
 * - Implementations SHOULD persist `CryptoKey` objects whose private material
 *   is non-extractable wherever the underlying storage layer can carry one.
 * - Adapters MUST NOT log, transmit, or otherwise emit private key material
 *   over any boundary outside this abstraction.
 */
export interface KeyStore {
  /**
   * Persist a private key for the given Account. If a key already exists
   * for `accountId`, it is overwritten.
   *
   * The accepted shape is `CryptoKey` for ECDSA P-256 schemes, or
   * `CryptoKeyPair` if the adapter wants to keep both halves of the pair
   * (the public half is cheap and convenient for callers that need it).
   */
  put(accountId: string, key: CryptoKey | CryptoKeyPair): Promise<void>;

  /**
   * Return the stored key for `accountId`, or `null` if none is present.
   *
   * Adapters return whichever `CryptoKey | CryptoKeyPair` shape was stored
   * by the matching `put` call.
   */
  get(accountId: string): Promise<CryptoKey | CryptoKeyPair | null>;

  /**
   * Remove the stored key for `accountId`. A no-op if no key is present.
   */
  delete(accountId: string): Promise<void>;

  /**
   * Return whether a key is currently persisted for `accountId`.
   */
  has(accountId: string): Promise<boolean>;

  /**
   * Return the set of Account IDs for which a key is currently persisted.
   * Order is unspecified; callers must not depend on it.
   */
  list(): Promise<string[]>;
}

/**
 * Database name used by the web (IndexedDB) adapter. Exported so that
 * tooling and tests can reference a single canonical name.
 */
export const KEY_STORE_DB_NAME = "link-keystore";

/**
 * Object store name used by the web (IndexedDB) adapter.
 */
export const KEY_STORE_OBJECT_STORE = "keys";

/**
 * Schema version for the IndexedDB database. Bump only when the on-disk
 * record shape changes; pair every bump with an upgrade migration in the
 * web adapter.
 */
export const KEY_STORE_DB_VERSION = 1;
